import React from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#0047FF", "#FF0054", "#FFD600", "#00E676", "#000000", "#888"];

function Card({ title, value, color = "bg-cef-yellow", testId }) {
  return (
    <div className={`neo-card p-4 ${color}`} data-testid={testId}>
      <div className="text-xs font-bold uppercase tracking-widest">{title}</div>
      <div className="mt-1 font-display font-black text-3xl">{(value || 0).toLocaleString("fr-BE")}</div>
    </div>
  );
}

export default function AdminStats() {
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    api.get("/admin/stats").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-neutral-500">Chargement…</div>;

  const t = data.totals;

  return (
    <div className="space-y-8" data-testid="stats-content">
      <div>
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Vue d'ensemble</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Card title="Inscrits (comptes)" value={t.users} color="bg-cef-blue text-white" testId="stat-users" />
          <Card title="Relais locaux" value={t.relais_locaux} color="bg-cef-green" testId="stat-relais" />
          <Card title="Réponses consultations" value={t.consultations_completed} color="bg-cef-yellow" testId="stat-consult-completed" />
          <Card title="Initiatives" value={t.initiatives} color="bg-cef-pink text-white" testId="stat-initiatives" />
          <Card title="Témoignages publiés" value={t.testimonials_approved} color="bg-white" testId="stat-temoignages" />
          <Card title="Témoignages en attente" value={t.testimonials_pending} color="bg-cef-yellow" testId="stat-temoignages-pending" />
          <Card title="Inscriptions newsletter" value={t.join_requests} color="bg-white" testId="stat-joins" />
          <Card title="Écoles actives" value={t.schools_active} color="bg-black text-white" testId="stat-schools" />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="neo-card p-5 bg-white">
          <h3 className="font-display font-black text-lg uppercase tracking-tight mb-3">Inscrits par province</h3>
          <div className="h-64 w-full min-h-[256px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_province}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="province" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#0047FF" stroke="#000" strokeWidth={1} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="neo-card p-5 bg-white">
          <h3 className="font-display font-black text-lg uppercase tracking-tight mb-3">Inscrits par rôle</h3>
          <div className="h-64 w-full min-h-[256px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.by_role} dataKey="count" nameKey="role" outerRadius={80} stroke="#000" strokeWidth={2}>
                  {data.by_role.map((entry) => <Cell key={entry.role} fill={COLORS[Math.abs(entry.role.charCodeAt(0)) % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="neo-card p-5 bg-white">
        <h3 className="font-display font-black text-lg uppercase tracking-tight mb-3">Top écoles (par nombre d'inscrits)</h3>
        {data.by_school.length === 0 ? <div className="text-neutral-500 text-sm">Aucun élève n'a encore renseigné son école.</div> : (
          <div className="h-72 w-full min-h-[288px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_school} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="school_name" type="category" tick={{ fontSize: 11 }} width={180} />
                <Tooltip />
                <Bar dataKey="count" fill="#FF0054" stroke="#000" strokeWidth={1} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="neo-card p-5 bg-white">
        <h3 className="font-display font-black text-lg uppercase tracking-tight mb-3">Évolution des inscriptions (30 derniers jours)</h3>
        {data.signups_over_time.length === 0 ? <div className="text-neutral-500 text-sm">Pas encore de données.</div> : (
          <div className="h-64 w-full min-h-[256px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.signups_over_time}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#0047FF" strokeWidth={3} dot={{ fill: "#FF0054", stroke: "#000", strokeWidth: 1, r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
